// Abrir la fuente dentro de Argus Team (diseño 4a §2.9; corte 4b, docs/producto/diseno-etapa4b.md §5): qué se puede abrir de cada
// fuente citada, como datos. La SPA tiene un solo visor, <VerFuente>, que pinta esto. Con la llave de un acto pide
// nexo.evidencia({runId, actoId}), con runId tomado de cortes.registro.corrida, y la imagen de su página; con la de un párrafo,
// nexo.evidencia({versionId, fragmentId}); con la de un documento, nuestra copia (texto y archivo); con la de la lista del SAT, la fila
// guardada; la ficha de OFAC se lee por su entidad. Sin llave se muestra con lo que ya trae el documento.
import type { Fuente, Hallazgos } from '../hallazgos/tipos.ts';

/** Documentos oficiales: comunicados, avisos y organigramas del Tesoro, y lo de FinCEN. */
export type ClaseDocumento = 'comunicado_tesoro' | 'aviso_ofac' | 'organigrama_ofac' | 'fincen';

export type FuenteAbrible =
  /** Un acto del Registro Público de Comercio que se lee completo con nexo.evidencia, con la imagen de su página (4b). */
  | { tipo: 'acto'; runId: string; actoId: number; fecha: string | null }
  /** Un acta sin llave (camino paso a paso): documento, página, fecha, cita y comprobaciones de la fuente. */
  | {
    tipo: 'acta';
    documento: string | null;
    pagina: number | null;
    fecha: string | null;
    cita: string | null;
    verificacion: { texto_presente: boolean; ubicacion: boolean | null; atribucion: boolean | null } | null;
  }
  /**
   * El registro de una entidad en la lista de OFAC: lo que trae `sancionados[ref]` y, con `entidadId`, nuestra copia completa del
   * registro (4b). La liga oficial es complemento: el sitio de OFAC no se deja incrustar.
   */
  | { tipo: 'lista_ofac'; fecha: string | null; consultado: string | null; url: string | null; sancionado: string | null; entidadId: string | null }
  /** Un comunicado del Tesoro sin llave: la cita (hasta 300 caracteres) y su liga. */
  | { tipo: 'comunicado'; fecha: string | null; cita: string | null; url: string | null }
  /** Un párrafo de un comunicado o de FinCEN con su llave (4b): el párrafo, su traducción y el documento completo. */
  | { tipo: 'fragmento'; clase: ClaseDocumento; versionId: number; fragmentId: string; titulo: string | null; fecha: string | null; url: string | null }
  /** Un documento oficial con la llave de su versión (4b): nuestra copia (texto y archivo) y la liga oficial. */
  | { tipo: 'documento'; clase: ClaseDocumento; versionId: number; titulo: string | null; fecha: string | null; url: string | null }
  /** Avisos, organigramas y órdenes de FinCEN sin llave: título, fecha y liga oficial. */
  | { tipo: 'documento_oficial'; clase: 'aviso_ofac' | 'organigrama_ofac' | 'fincen'; titulo: string | null; fecha: string | null; url: string | null }
  /** La lista del SAT: la fecha y, con `flagId`, nuestra copia de la fila (4b). */
  | { tipo: 'lista_sat'; fecha: string | null; url: string | null; flagId: string | null }
  /**
   * Una fuente que no se abre: un tipo que esta versión de la pantalla no conoce (la base puede mandar tipos nuevos antes de que la
   * pantalla los sepa abrir; corte 0 de docs/producto/diseno-ficha-certificados-sat.md). La frase se muestra sin «Ver la fuente».
   */
  | { tipo: 'no_se_abre'; fecha: string | null };

export interface Contexto {
  /** cortes.registro.corrida; sin corrida vigente, la llave no se puede usar y el acta se muestra con lo que trae. */
  corrida: string | null;
  /** Referencia del sancionado cuya ficha se abre, si la fuente es su registro en la lista. */
  sancionado?: string | null;
}

/** El contexto de un documento: la corrida con la que se abren las llaves. */
export function contextoDe(doc: Hallazgos, sancionado: string | null = null): Contexto {
  return { corrida: doc.cortes.registro?.corrida ?? null, sancionado };
}

type Llave = NonNullable<Fuente['llave']>;
const llaveDe = (f: Fuente): Llave | null => (f.llave && typeof f.llave === 'object' ? f.llave : null);
const ENTIDAD = /^entity:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/;

/** Qué se puede abrir de una fuente. */
export function abrible(f: Fuente, ctx: Contexto): FuenteAbrible {
  const fecha = f.fecha ?? null;
  const k = llaveDe(f);
  switch (f.tipo) {
    case 'acta':
      if (k && 'actoId' in k && ctx.corrida) return { tipo: 'acto', runId: ctx.corrida, actoId: k.actoId, fecha };
      return {
        tipo: 'acta',
        documento: f.documento ?? null,
        pagina: f.pagina ?? null,
        fecha,
        cita: f.cita ? f.cita.slice(0, 300) : null,
        verificacion: f.verificacion ?? null,
      };
    case 'lista_ofac': {
      // La llave nombra a otra entidad (de con quién se relaciona un sancionado); sin ella, es el sancionado del contexto.
      const sancionado = k && 'entidadId' in k ? `entity:${k.entidadId}` : ctx.sancionado ?? null;
      const entidadId = sancionado ? ENTIDAD.exec(sancionado)?.[1] ?? null : null;
      return { tipo: 'lista_ofac', fecha, consultado: f.consultado ?? null, url: f.url ?? null, sancionado, entidadId };
    }
    case 'comunicado_tesoro':
    case 'aviso_ofac':
    case 'organigrama_ofac':
    case 'fincen': {
      const titulo = f.documento ?? null, url = f.url ?? null;
      if (k && 'fragmentId' in k) return { tipo: 'fragmento', clase: f.tipo, versionId: k.versionId, fragmentId: k.fragmentId, titulo, fecha, url };
      if (k && 'versionId' in k) return { tipo: 'documento', clase: f.tipo, versionId: k.versionId, titulo, fecha, url };
      if (f.tipo === 'comunicado_tesoro') return { tipo: 'comunicado', fecha, cita: f.cita ? f.cita.slice(0, 300) : null, url };
      return { tipo: 'documento_oficial', clase: f.tipo, titulo, fecha, url };
    }
    case 'lista_sat':
      return { tipo: 'lista_sat', fecha, url: f.url ?? null, flagId: k && 'flagId' in k ? k.flagId : null };
    default:
      return { tipo: 'no_se_abre', fecha };
  }
}

/** Si la fuente se puede abrir (si no, la pantalla no ofrece «Ver la fuente»). */
export const seAbre = (a: FuenteAbrible): boolean => a.tipo !== 'no_se_abre';
